// Imports the link classifier directly rather than going through the
// renderer's `useWikiMarkdown.ts` re-export — the renderer also imports
// from here, so an indirect path through it would form a circular
// module graph and (under Vite) re-evaluate this file twice, which
// raises an "Identifier 'injectInlineCitations' has already been
// declared" runtime error during SPA hydration.
import { resolveWikiLink } from './useWikiMarkdown.linkClassifier'

// Inline citation marker DOM contract — owned here. The renderer
// emits markers with this class + these data attributes; consumers
// (e.g. CitationHoverManager) call `parseCitationMarker` to read
// them. Centralising the names + the parse means renaming an
// attribute is a one-file edit, not a silent break.
export const CITATION_MARKER_CLASS = 'wiki-citation-marker'
const DATA_CITE_PATH = 'data-cite-path'
const DATA_CITE_INDEX = 'data-cite-index'

// Anchor selector for the inner `<a>` inside the marker `<sup>`.
// Hover/focus delegation queries the closest match against this so
// events on nested DOM (the `[N]` text node) bubble up to the right
// element.
export const CITATION_MARKER_ANCHOR_SELECTOR = `.${CITATION_MARKER_CLASS} a`

export interface CitationRef {
  path: string
  // Resolved title from the catalog so the hover tooltip can show
  // human-readable text instead of the raw path.
  title?: string
}

// Parsed shape that hover-card consumers receive. Lives on the
// citation module so consumers don't have to reconstruct the contract.
export interface CitationMarkerData {
  path: string
  index: number
}

// Reads the marker contract off any element that could be a marker
// anchor (or contain one). Returns null when the element isn't a
// marker, the path is missing/blank, or the index is malformed —
// callers can `if (!parsed) return` without cluttering the call site
// with dataset reads + fallback parsing.
export function parseCitationMarker(target: EventTarget | null): CitationMarkerData | null {
  if (!(target instanceof HTMLElement)) return null
  const anchor = target.closest<HTMLElement>(CITATION_MARKER_ANCHOR_SELECTOR)
  if (!anchor) return null
  const path = anchor.getAttribute(DATA_CITE_PATH) ?? ''
  if (!path.trim()) return null
  const indexAttr = anchor.getAttribute(DATA_CITE_INDEX) ?? '0'
  const index = Number.parseInt(indexAttr, 10) || 0
  return { path, index }
}

// Walks the rendered HTML and, for each citation, finds the FIRST
// `<a href>` whose target resolves to that citation's path and inserts
// a `<sup>` marker right after. The marker is itself a real link to the
// page so click-to-navigate works without JS, plus a `title` attribute
// for native browser hover-tooltips and a marker class so the host
// component can layer NPopover hover cards on top.
//
// Implementation note: we operate on the rendered HTML string rather
// than the markdown AST because the auto-link pass already classified
// each href, so HTML is the easiest place to match canonical /p/<path>
// hrefs against the citations list. A regex over `<a … href="X">…</a>`
// is robust enough — markdown-it's link_open emits a normalised shape
// and we already escape attribute content via markdown-it's defaults.
export function injectInlineCitations(
  html: string,
  citations: readonly CitationRef[],
  currentPath: string | undefined,
  knownPaths: readonly string[],
): string {
  if (citations.length === 0) return html
  const indexByHref = new Map<string, number>()
  citations.forEach((c, idx) => {
    const resolved = resolveWikiLink(c.path, currentPath ?? '', knownPaths)
    if (resolved) indexByHref.set(resolved, idx + 1)
  })
  if (indexByHref.size === 0) return html

  const seen = new Set<number>()
  return html.replaceAll(/<a\b[^>]*\bhref="([^"]+)"[^>]*>[^<]*<\/a>/g, (match, href: string) => {
    const n = indexByHref.get(href)
    if (!n || seen.has(n)) return match
    seen.add(n)
    const titleAttr = citationTitle(citations[n - 1])
    return `${match}<sup class="${CITATION_MARKER_CLASS}"><a href="${href}" data-wiki-link="true" ${DATA_CITE_INDEX}="${n}" ${DATA_CITE_PATH}="${escapeAttr(citations[n - 1].path)}" title="${escapeAttr(titleAttr)}">[${n}]</a></sup>`
  })
}

function citationTitle(citation: CitationRef): string {
  if (citation.title && citation.title !== citation.path) return `${citation.title} — ${citation.path}`
  return citation.path
}

function escapeAttr(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')
}
